<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { api, jsonBody } from '../../services/api'

const route = useRoute()
interface QuoteDetail {
  quote: {
    id: string
    name: string
    text: string
    description: string | null
    isActive: boolean
  }
  stats: Record<string, number>
  bottles: Array<{ id: string; name: string; status: string; createdAt: string }>
}
const data = ref<QuoteDetail | null>(null)
const editing = ref(false)
const form = ref({
  name: '',
  text: '',
  description: '',
  isActive: true,
  physicalQuoteReplaced: false,
})
const error = ref('')
async function load() {
  const loaded = await api<QuoteDetail>(`/admin/quotes/${String(route.params.id)}`)
  data.value = loaded
  form.value = {
    name: loaded.quote.name,
    text: loaded.quote.text,
    description: loaded.quote.description ?? '',
    isActive: loaded.quote.isActive,
    physicalQuoteReplaced: false,
  }
}
async function save() {
  try {
    await api(`/admin/quotes/${String(route.params.id)}`, {
      method: 'PATCH',
      ...jsonBody(form.value),
    })
    editing.value = false
    await load()
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : 'Quote could not be saved.'
  }
}
onMounted(load)
</script>

<template>
  <div v-if="data">
    <header class="admin-heading">
      <div>
        <RouterLink class="breadcrumb" to="/admin/quotes">← Quotes</RouterLink>
        <h1>{{ data.quote.name }}</h1>
        <p>Reusable message and circulation history.</p>
      </div>
      <button class="button secondary" @click="editing = !editing">
        {{ editing ? 'Cancel' : 'Edit quote' }}
      </button>
    </header>
    <p v-if="error" class="form-error banner">{{ error }}</p>
    <form v-if="editing" class="admin-card editor-form" @submit.prevent="save">
      <div class="form-grid">
        <label>Name<input v-model="form.name" required /></label
        ><label class="span-2">Text<textarea v-model="form.text" rows="4" required /></label
        ><label class="span-2">Description<textarea v-model="form.description" rows="3" /></label
        ><label class="check-label span-2"
          ><input v-model="form.physicalQuoteReplaced" type="checkbox" /><span
            >I confirm every circulated physical quote will be replaced if the text changed.</span
          ></label
        >
      </div>
      <button class="button primary">Save changes</button>
    </form>
    <section class="quote-feature admin-card">
      <p class="eyebrow">The message</p>
      <blockquote>“{{ data.quote.text }}”</blockquote>
      <p>{{ data.quote.description }}</p>
    </section>
    <section class="metric-grid compact-metrics">
      <article v-for="(value, key) in data.stats" :key="key">
        <span>{{ String(key).replaceAll('_', ' ') }}</span
        ><strong>{{ value }}</strong>
      </article>
    </section>
    <section class="admin-card table-card">
      <div class="card-heading"><h2>Bottles carrying this quote</h2></div>
      <div class="data-table">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Status</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="bottle in data.bottles" :key="bottle.id">
              <td>
                <RouterLink :to="`/admin/bottles/${bottle.id}`">{{ bottle.name }}</RouterLink>
              </td>
              <td>
                <span class="status-pill" :class="bottle.status">{{ bottle.status }}</span>
              </td>
              <td>{{ new Date(bottle.createdAt).toLocaleDateString() }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
</template>
